import {
  ChangeDetectorRef,
  Component,
  InjectionToken,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  input,
  viewChild
} from '@angular/core';
import { takeUntil, tap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { TranslateDirective, TranslatePipe as NgxTranslatePipe } from '@ngx-translate/core';
import { DashboardRestService } from '@tdwb/lazy/lti';
import {
  PageHeaderComponent,
  PageHeaderTitleComponent,
  PageHeaderToolbarComponent,
  RouterService
} from '@tdwb/webbroker/core';
import {
  Breakpoint,
  BreakpointState,
  MediaQueryService,
  ModalService,
  SecurityService,
  ClientProfileService,
  FinePrintService,
  GoalStatusType,
  InvestmentGoal,
  LtiConfig,
  IfFeatureService, DataAnalyticsTaggingService, DataAnalyticsTaggingConstants,
  ToolbarComponent,
  ToolbarItemHelpComponent,
  StickyPanelTopComponent,
  MobileToolbarComponent,
  PagePanelComponent,
  LtiPagePanelComponent,
  ButtonComponent,
  BlockDirective,
  HrefDirective,
  PaginationComponent,
  CallOutComponent,
  IconComponent,
  CardComponent,
  CardContentComponent,
  ImagePlaceholderComponent,
  ConfirmComponent,
  ConfirmTitleComponent,
  ConfirmBodyComponent,
  ConfirmButtonsComponent,
  ConfirmButtonComponent,
  ModalCloseDirective
} from '@tdwb/core';

import { DashboardGoalComponent } from './dashboard-goal.component';
import { DashboardAsStartPageCalloutComponent } from './callout/dashboard-as-start-page-callout.component';

export const LTI_DASHBOARD_CONFIGURATION = new InjectionToken<LtiConfig>('ltiDashboardConfiguration');

@Component({
  selector: 'td-wb-lti-dashboard',
  templateUrl: 'dashboard.component.html',
  styleUrls: ['dashboard.component.scss'],
  imports: [
    TranslateDirective,
    NgxTranslatePipe,
    PageHeaderComponent,
    PageHeaderTitleComponent,
    PageHeaderToolbarComponent,
    ToolbarComponent,
    ToolbarItemHelpComponent,
    StickyPanelTopComponent,
    MobileToolbarComponent,
    PagePanelComponent,
    LtiPagePanelComponent,
    ButtonComponent,
    BlockDirective,
    HrefDirective,
    PaginationComponent,
    CallOutComponent,
    IconComponent,
    CardComponent,
    CardContentComponent,
    ImagePlaceholderComponent,
    ConfirmComponent,
    ConfirmTitleComponent,
    ConfirmBodyComponent,
    ConfirmButtonsComponent,
    ConfirmButtonComponent,
    ModalCloseDirective,
    DashboardGoalComponent,
    DashboardAsStartPageCalloutComponent
  ]
})

export class DashboardComponent implements OnInit, OnDestroy {
  private readonly ltiConfig = inject(LTI_DASHBOARD_CONFIGURATION);
  private readonly dashboardRestService = inject(DashboardRestService);
  private readonly clientProfileService = inject(ClientProfileService);
  private readonly routerService = inject(RouterService);
  private readonly modalService = inject(ModalService);
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly securityService = inject(SecurityService);
  private readonly finePrintService = inject(FinePrintService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly featureService = inject(IfFeatureService);
  private readonly dataAnalyticsTaggingService = inject(DataAnalyticsTaggingService);

  private static readonly ADD_GOAL_PAGE: string = 'page.lti.goal-selection';
  private static readonly MAX_GOALS_HEADER: string = 'MAX_GOALS_HEADER';
  private static readonly NO_ACCOUNTS_HEADER: string = 'NO_ACCOUNTS_HEADER';
  readonly goals = input<Array<InvestmentGoal>>();
  private readonly addGoalMessageQuery = viewChild<TemplateRef<unknown>>('addGoalMessage');
  addGoalMessage: TemplateRef<unknown> | undefined;
  addGoalMessageHeader: string;
  clientName: string;
  displayedGoals: Array<InvestmentGoal>;
  goalsDisplayedPerPage: number = 5;
  private maxGoals: number = 300;
  disableAddGoal: boolean = false;
  smallView: boolean = false;
  mediumView: boolean = false;
  switchGiem: boolean;

  private selectedPage: number = 0;
  private readonly destroy: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.switchGiem = this.featureService.getFeatureFlag<boolean>('switchGiem');
    this.clientName = this.clientProfileService.getFirstName(true);
    if (this.ltiConfig !== undefined) {
      const pageSize = this.ltiConfig.dashboardPageSize;
      const maxGoals = this.ltiConfig.maxGoals;
      this.goalsDisplayedPerPage = pageSize !== undefined && pageSize > 0 ? pageSize : 5;
      this.maxGoals = maxGoals !== undefined && maxGoals > 0 ? maxGoals : 300;
    }
    if (this.goals() !== undefined) {
      this.setDisplayedGoals();
      this.fetchGoalDetails();
    }
    this.mediaQueryService.observe(Breakpoint.SM, Breakpoint.MD)
      .pipe(takeUntil(this.destroy))
      .subscribe((breakpointState: BreakpointState): void => {
        this.smallView = breakpointState.isBreakpointMatch(Breakpoint.SM);
        this.mediumView = breakpointState.isBreakpointMatch(Breakpoint.MD);
        this.changeDetectorRef.markForCheck();
      });
    this.finePrintService.setNamespace('tdWb.lti.dashboard.fineprints');
    this.finePrintService.setFinePrints(
      { disclaimers: ['DEFAULT_DISCLAIMER'] });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.finePrintService.clearFinePrints();
  }

  hasGoals(): boolean {
    const goals = this.goals();
    return goals !== undefined && goals.length > 0;
  }

  onAddGoalClick(): void {
    this.dataAnalyticsTaggingService.emitTaggingEvent(DataAnalyticsTaggingConstants.getLinkClickEventData('tdw:wbt:diy:add a goal'));
    this.disableAddGoal = true;
    const goals = this.goals();
    if (goals !== undefined && goals.length >= this.maxGoals) {
      this.addGoalMessageHeader = DashboardComponent.MAX_GOALS_HEADER;
      this.modalService.open(this.addGoalMessage ?? this.addGoalMessageQuery(), {
        size: 'extra-small'
      });
    } else {
      this.dashboardRestService.hasLtiEligibleAccounts().subscribe((result: boolean): void => {
        if (result) {
          this.disableAddGoal = false;
          this.routerService.go(DashboardComponent.ADD_GOAL_PAGE);
        } else {
          this.disableAddGoal = false;
          this.addGoalMessageHeader = DashboardComponent.NO_ACCOUNTS_HEADER;
          this.modalService.open(this.addGoalMessage ?? this.addGoalMessageQuery(), {
            size: 'extra-small'
          });
        }
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  onPageClick(selectedPage: number): void {
    this.selectedPage = selectedPage;
    this.setDisplayedGoals();
    this.fetchGoalDetails();
  }

  showPagination(): boolean {
    const goals = this.goals();
    return goals !== undefined && goals.length > this.goalsDisplayedPerPage;
  }

  deleteGoal(goalId: string): Observable<void> {
    if (goalId !== undefined) {
      return this.dashboardRestService.deleteInvestmentGoal({ goalId }).pipe(tap((): void => {
        this.goals().forEach((goal: InvestmentGoal, index: number): void => {
          if (goal.id === goalId) {
            this.goals().splice(index, 1);
          }
        });
        const goalsAvailableToShow = this.selectedPage * this.goalsDisplayedPerPage;
        if (goalsAvailableToShow >= this.goals().length) {
          this.selectedPage = 0;
        }
        this.setDisplayedGoals();
      }));
    }
  }

  /*If dashboard has two or more than two goals, dashboard will show add goal button at the right corner. Otherwise dashboard
    * will show have other savings goals in mind panel */
  showAddGoalButton(): boolean {
    const goals = this.goals();
    return goals !== undefined ? goals.length >= 2 : false;
  }

  showAllEducationCards(): boolean {
    return this.securityService.hasPermission('GOALS_DASHBOARD_EDUCATION_CARDS');
  }

  private fetchGoalDetails(): void {
    const goalIdArray = [];
    if (this.displayedGoals !== undefined) {
      this.displayedGoals.forEach((goal: InvestmentGoal): void => {
        if (goal.status !== GoalStatusType.NO_MODEL && goal.status !== GoalStatusType.NO_PORTFOLIO
          && goal.investmentObjective === undefined) {
          goalIdArray.push(goal.id);
        }
      });
      if (goalIdArray.length > 0) {
        this.dashboardRestService.getGoalsWithObjective({ goalId: goalIdArray }).subscribe((data: Array<InvestmentGoal>): void => {
          this.updateGoalsWithDetails(data);
          this.changeDetectorRef.markForCheck();
        });
      }
    }
  }

  private updateGoalsWithDetails(updatedGoals: Array<InvestmentGoal>): void {
    if (updatedGoals !== undefined && updatedGoals instanceof Array) {
      const startIndex = this.getGoalStartIndex();
      const endIndex = this.getGoalEndIndex(startIndex);
      for (let i = startIndex; i < endIndex; i++) {
        const goal = this.goals()[i];
        if (goal !== undefined) {
          updatedGoals.forEach((updatedGoal: InvestmentGoal): void => {
            if (updatedGoal.id === goal.id) {
              this.goals()[i] = updatedGoal;
            }
          });
        }
      }
      this.setDisplayedGoals();
    }
  }

  private setDisplayedGoals(): void {
    const startIndex = this.getGoalStartIndex();
    const endIndex = this.getGoalEndIndex(startIndex);
    this.displayedGoals = this.goals().slice(startIndex, endIndex);
  }

  private getGoalStartIndex(): number {
    return this.selectedPage * this.goalsDisplayedPerPage;
  }

  private getGoalEndIndex(startIndex: number): number {
    return (startIndex + this.goalsDisplayedPerPage) < this.goals().length ?
      (startIndex + this.goalsDisplayedPerPage) : this.goals().length;
  }
}
